import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AiAgentKnowledgeBase } from '@lgg/isomorphic/types/__generated__/graphql';
import { Table, TableColumns, TableProps } from 'src/components/general/display/table';
import { getDefaultSortOrder, TableSortData } from 'src/components/general/table-helpers';
import {
  getCreatedAtColumn,
  getCreatedByColumn,
  getDeleteRowOption,
  getRowOptionsColumn,
  TableLightText,
} from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { useAuthorization } from 'src/hooks/use-authorization';

const Title = styled(Link)`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
`;

const SourcesCount = styled(TableLightText)`
  display: block;
  text-align: center;
`;

type KnowledgeBasesTableProps = {
  data: AiAgentKnowledgeBase[];
  sortData: TableSortData;
  onChange: TableProps<AiAgentKnowledgeBase>['onChange'];
  sectionUrl: string;
  onDelete: (knowledgeBase: AiAgentKnowledgeBase) => void;
};

export const KnowledgeBasesTable = memo<KnowledgeBasesTableProps>(
  ({ data, sortData, onChange, sectionUrl, onDelete }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const history = useHistory();
    const { hasPermission } = useAuthorization();
    const { key: sortKey, direction: sortDirection } = sortData;
    const canUpdate = hasPermission('ai:company.ai_agent_knowledge_base:update:all');
    const canDelete = hasPermission('ai:company.ai_agent_knowledge_base:delete:all');

    const columns: TableColumns<AiAgentKnowledgeBase>[] = [
      {
        title: t('aiAgents:pages.knowledgeBases.columnHeaders.title'),
        key: 'title',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(sortKey === 'title', sortDirection),
        render: (_, { id, title }) => (
          <Title
            to={`${sectionUrl}/knowledge-bases/${id}`}
            data-lgg-id="knowledge-bases-table-cell-title"
          >
            {title}
          </Title>
        ),
      },
      {
        title: t('aiAgents:pages.knowledgeBases.columnHeaders.sources'),
        key: 'sourcesCount',
        width: '135px',
        render: (_, { sourcesCount }) => (
          <SourcesCount data-lgg-id="knowledge-bases-table-cell-sources">
            {sourcesCount}
          </SourcesCount>
        ),
      },
      getCreatedAtColumn<AiAgentKnowledgeBase>({
        title: t('common:date'),
        sortData,
        testId: 'knowledge-bases-table-cell-date',
        width: '135px',
      }),
      getCreatedByColumn<AiAgentKnowledgeBase>({
        title: t('aiAgents:pages.knowledgeBases.columnHeaders.createdBy'),
        sortData,
        testId: 'knowledge-bases-table-cell-created-by',
      }),
      ...(canUpdate || canDelete
        ? [
            getRowOptionsColumn<AiAgentKnowledgeBase>((knowledgeBase) => [
              ...(canUpdate
                ? [
                    {
                      label: t('aiAgents:pages.knowledgeBases.options.rename'),
                      icon: 'edit',
                      'data-lgg-id': 'knowledge-base-option-rename',
                      onClick: () =>
                        history.push(
                          `${sectionUrl}/knowledge-bases/${knowledgeBase.id}?rename=true`,
                        ),
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    getDeleteRowOption({
                      label: t('common:delete'),
                      testId: 'knowledge-base-option-delete',
                      onClick: () => onDelete(knowledgeBase),
                    }),
                  ]
                : []),
            ]),
          ]
        : []),
    ];

    return (
      <Table
        rowKey={({ id }) => id}
        data-lgg-id="knowledge-bases-table"
        columns={columns}
        tableLayout="fixed"
        loading={false}
        dataSource={data}
        pagination={false}
        showHeader={true}
        scroll={{ x: 1069 }}
        rowClassName="knowledge-bases-table-row"
        onChange={onChange}
      />
    );
  },
);
